import { FormEvent, ReactElement, useState } from "react";
import { Head, router } from "@inertiajs/react";
import DeskLayout from "@/layouts/DeskLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

interface CompanySetupProps {
  errors?: {
    company_name?: string | string[];
    country?: string | string[];
  };
  values?: {
    company_name?: string;
    country?: string;
  };
}

export default function CompanySetup({ errors = {}, values = {} }: CompanySetupProps) {
  const initialCompanyName = values.company_name ?? "";

  const [companyName, setCompanyName] = useState(initialCompanyName);
  const [country, setCountry] = useState(values.country ?? "");
  const [loading, setLoading] = useState(false);
  const companyNameError = Array.isArray(errors.company_name)
    ? errors.company_name.join(", ")
    : errors.company_name;
  const countryError = Array.isArray(errors.country) ? errors.country.join(", ") : errors.country;
  const countries = [{ value: "SY", label: "سوريا" }];

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);

    router.post(
      "/desk/company-setup",
      {
        account: {
          company_name: companyName,
          country,
        },
      },
      {
        onFinish: () => setLoading(false),
      }
    );
  };

  return (
    <>
      <Head title="إعداد الشركة" />
      <div className="flex min-h-[calc(100svh-3rem)] items-center justify-center rounded-3xl bg-muted/30 p-6">
        <Card className="w-full max-w-lg border border-border/60 bg-card/95 shadow-xl">
          <CardHeader className="space-y-2 text-center">
            <CardTitle className="text-3xl font-semibold">لنبدأ بالشركة</CardTitle>
            <CardDescription className="text-base">
              أدخل اسم الشركة وبلدها لإنشاء مساحة العمل الخاصة بك.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="company-name">اسم الشركة</Label>
                <Input
                  id="company-name"
                  type="text"
                  value={companyName}
                  onChange={(event) => setCompanyName(event.target.value)}
                  required
                  autoComplete="organization"
                />
                {companyNameError && <p className="text-sm text-destructive">{companyNameError}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="company-country">البلد</Label>
                <select
                  id="company-country"
                  value={country}
                  onChange={(event) => setCountry(event.target.value)}
                  required
                  className="h-9 w-full rounded-md border border-input bg-input px-3 text-sm text-foreground"
                >
                  <option value="" disabled>
                    اختر البلد
                  </option>
                  {countries.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
                {countryError && <p className="text-sm text-destructive">{countryError}</p>}
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "جاري الإعداد..." : "إنشاء الشركة"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

CompanySetup.layout = (page: ReactElement) => <DeskLayout hideSidebar>{page}</DeskLayout>;
